<script setup>
import { ref, computed, watch } from "vue";
import {
  SparklesIcon,
  SwatchIcon,
  EyeDropperIcon,
  ArrowPathIcon,
  PaintBrushIcon,
  AdjustmentsHorizontalIcon,
  BeakerIcon,
  StarIcon,
  DevicePhoneMobileIcon,
  ServerIcon,
  BoltIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/vue/24/outline";

// Props & Emits
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      enabled: false,
      preset: null,
      dotsType: "square",
      cornersSquareType: "square",
      cornersDotType: "square",
      darkColor: null,
      lightColor: null,
      backgroundColor: null,
      gradientColors: [],
      gradientType: "linear",
      gradientRotation: 0,
      type: null,
      dotScale: 0.8,
      cornerRadius: 20,
      iconStyle: "square",
      iconScale: 0.4,
      logoScale: 0.4,
    }),
  },
  availablePresets: {
    type: Array,
    default: () => [],
  },
  frontendAvailable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:modelValue", "preset-apply", "quick-style"]);

// Local reactive state
const localOptions = ref({ ...props.modelValue });
const activeSection = ref("presets"); // 'presets', 'advanced', 'gradients', 'colors'
const showPreview = ref(false);

// Style configurations
const quickStyles = [
  {
    id: "dots",
    name: "Dots",
    description: "Modern circular dots",
    icon: "⚫",
    style: { dotsType: "dots" },
    frontendSupport: "excellent",
    preview: "Clean and modern",
  },
  {
    id: "rounded",
    name: "Rounded",
    description: "Soft rounded corners",
    icon: "⬜",
    style: { dotsType: "rounded", cornersSquareType: "extra-rounded" },
    frontendSupport: "excellent",
    preview: "Friendly and approachable",
  },
  {
    id: "gradient",
    name: "Gradient",
    description: "Colorful gradients",
    icon: "🌈",
    style: { gradientColors: ["#ff6b6b", "#4ecdc4"], gradientType: "linear" },
    frontendSupport: "excellent",
    preview: "Eye-catching and vibrant",
  },
  {
    id: "classy",
    name: "Classy",
    description: "Sophisticated style",
    icon: "💎",
    style: { dotsType: "classy-rounded", cornersSquareType: "extra-rounded" },
    frontendSupport: "excellent",
    preview: "Professional and elegant",
  },
];

const advancedStyles = [
  {
    name: "Dots Type",
    key: "dotsType",
    type: "select",
    options: [
      {
        value: "square",
        label: "Square",
        description: "Standard square modules",
      },
      { value: "dots", label: "Dots", description: "Circular dots" },
      { value: "rounded", label: "Rounded", description: "Rounded squares" },
      {
        value: "extra-rounded",
        label: "Extra Rounded",
        description: "Very rounded squares",
      },
      { value: "classy", label: "Classy", description: "Diamond-like shapes" },
      {
        value: "classy-rounded",
        label: "Classy Rounded",
        description: "Rounded diamonds",
      },
    ],
    frontendSupport: "excellent",
  },
  {
    name: "Corner Square Type",
    key: "cornersSquareType",
    type: "select",
    options: [
      {
        value: "square",
        label: "Square",
        description: "Standard square corners",
      },
      {
        value: "extra-rounded",
        label: "Extra Rounded",
        description: "Very rounded corners",
      },
      { value: "dot", label: "Dot", description: "Dot-style corners" },
    ],
    frontendSupport: "excellent",
  },
  {
    name: "Corner Dot Type",
    key: "cornersDotType",
    type: "select",
    options: [
      { value: "square", label: "Square", description: "Square corner dots" },
      { value: "dot", label: "Dot", description: "Round corner dots" },
    ],
    frontendSupport: "excellent",
  },
];

const gradientPresets = [
  { name: "Sunset", colors: ["#FF8A00", "#DA0175"], rotation: 45 },
  { name: "Ocean", colors: ["#00F5FF", "#0085FF"], rotation: 0 },
  { name: "Forest", colors: ["#11998E", "#38EF7D"], rotation: 135 },
  { name: "Purple Haze", colors: ["#8B5CF6", "#EC4899"], rotation: 90 },
  { name: "Fire", colors: ["#FF6B6B", "#FFE66D"], rotation: 45 },
  { name: "Cool Blue", colors: ["#4ECDC4", "#44A08D"], rotation: 0 },
  { name: "Royal", colors: ["#667eea", "#764ba2"], rotation: 135 },
  { name: "Emerald", colors: ["#059669", "#10b981"], rotation: 90 },
];

const colorPresets = [
  { name: "Classic", dark: "#000000", light: "#ffffff", bg: "#ffffff" },
  { name: "Blue", dark: "#1e40af", light: "#dbeafe", bg: "#f0f9ff" },
  { name: "Green", dark: "#059669", light: "#d1fae5", bg: "#ecfdf5" },
  { name: "Purple", dark: "#7c3aed", light: "#e9d5ff", bg: "#faf5ff" },
  { name: "Red", dark: "#dc2626", light: "#fee2e2", bg: "#fef2f2" },
  { name: "Orange", dark: "#ea580c", light: "#fed7aa", bg: "#fff7ed" },
  { name: "Pink", dark: "#db2777", light: "#fce7f3", bg: "#fdf2f8" },
  { name: "Indigo", dark: "#4338ca", light: "#e0e7ff", bg: "#eef2ff" },
];

// Computed properties
const sections = computed(() => [
  { id: "presets", label: "Presets", icon: StarIcon },
  { id: "advanced", label: "Advanced", icon: AdjustmentsHorizontalIcon },
  { id: "gradients", label: "Gradients", icon: SwatchIcon },
  { id: "colors", label: "Colors", icon: EyeDropperIcon },
]);

const hasActiveStyles = computed(() => {
  return (
    localOptions.value.enabled &&
    (localOptions.value.preset ||
      localOptions.value.dotsType !== "square" ||
      localOptions.value.cornersSquareType !== "square" ||
      localOptions.value.gradientColors.length > 0 ||
      localOptions.value.darkColor ||
      localOptions.value.backgroundColor)
  );
});

const frontendCompatible = computed(() => {
  if (!props.frontendAvailable) return false;

  // Check if all current styles are frontend compatible
  const incompatibleFeatures = [
    localOptions.value.iconStyle && localOptions.value.iconStyle !== "square",
    localOptions.value.type &&
      !["dots", "rounded", "gradient"].includes(localOptions.value.type),
    // Add other backend-only features here
  ];

  return !incompatibleFeatures.some(Boolean);
});

const styleComplexity = computed(() => {
  let score = 0;

  if (localOptions.value.preset) score += 1;
  if (localOptions.value.dotsType !== "square") score += 1;
  if (localOptions.value.cornersSquareType !== "square") score += 1;
  if (localOptions.value.gradientColors.length > 0) score += 2;
  if (localOptions.value.darkColor) score += 1;
  if (localOptions.value.backgroundColor) score += 1;

  if (score === 0) return { level: "none", label: "Basic", color: "gray" };
  if (score <= 2) return { level: "simple", label: "Simple", color: "green" };
  if (score <= 4)
    return { level: "moderate", label: "Moderate", color: "yellow" };
  return { level: "complex", label: "Complex", color: "red" };
});

// Methods
const updateOptions = () => {
  emit("update:modelValue", { ...localOptions.value });
};

const toggleStyling = () => {
  localOptions.value.enabled = !localOptions.value.enabled;
  if (localOptions.value.enabled && !hasAnyStyles()) {
    // Apply a default style when enabling
    localOptions.value.dotsType = "dots";
  }
  updateOptions();
};

const hasAnyStyles = () => {
  return (
    localOptions.value.preset ||
    localOptions.value.dotsType !== "square" ||
    localOptions.value.cornersSquareType !== "square" ||
    localOptions.value.gradientColors.length > 0 ||
    localOptions.value.darkColor ||
    localOptions.value.backgroundColor
  );
};

const applyQuickStyle = (styleConfig) => {
  localOptions.value.enabled = true;
  Object.assign(localOptions.value, styleConfig.style);
  updateOptions();
  emit("quick-style", styleConfig.id);
};

const applyPreset = (presetName) => {
  localOptions.value.enabled = true;
  localOptions.value.preset = presetName;
  // Clear other conflicting styles when applying preset
  localOptions.value.dotsType = "square";
  localOptions.value.cornersSquareType = "square";
  localOptions.value.gradientColors = [];
  localOptions.value.type = null;
  updateOptions();
  emit("preset-apply", presetName);
};

const applyGradientPreset = (preset) => {
  localOptions.value.enabled = true;
  localOptions.value.gradientColors = [...preset.colors];
  localOptions.value.gradientRotation = preset.rotation || 0;
  localOptions.value.gradientType = "linear";
  updateOptions();
};

const applyColorPreset = (preset) => {
  localOptions.value.darkColor = preset.dark;
  localOptions.value.lightColor = preset.light;
  localOptions.value.backgroundColor = preset.bg;
  updateOptions();
};

const addGradientColor = () => {
  if (localOptions.value.gradientColors.length < 5) {
    localOptions.value.gradientColors.push("#ffffff");
    updateOptions();
  }
};

const removeGradientColor = (index) => {
  if (localOptions.value.gradientColors.length > 2) {
    localOptions.value.gradientColors.splice(index, 1);
    updateOptions();
  }
};

const swapGradientColors = () => {
  if (localOptions.value.gradientColors.length >= 2) {
    const temp = localOptions.value.gradientColors[0];
    localOptions.value.gradientColors[0] = localOptions.value.gradientColors[1];
    localOptions.value.gradientColors[1] = temp;
    updateOptions();
  }
};

const resetStyles = () => {
  localOptions.value = {
    enabled: false,
    preset: null,
    dotsType: "square",
    cornersSquareType: "square",
    cornersDotType: "square",
    darkColor: null,
    lightColor: null,
    backgroundColor: null,
    gradientColors: [],
    gradientType: "linear",
    gradientRotation: 0,
    type: null,
    dotScale: 0.8,
    cornerRadius: 20,
    iconStyle: "square",
    iconScale: 0.4,
    logoScale: 0.4,
  };
  updateOptions();
};

const getSupportIcon = (support) => {
  switch (support) {
    case "excellent":
      return "✅";
    case "good":
      return "✅";
    case "limited":
      return "⚠️";
    case "none":
      return "❌";
    default:
      return "❓";
  }
};

// Watchers
watch(localOptions, updateOptions, { deep: true });

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      localOptions.value = { ...newValue };
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="space-y-6">
    <!-- Header with Style Toggle -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <SparklesIcon class="w-5 h-5 text-purple-600" />
        <h3 class="text-lg font-semibold text-gray-800">Style Options</h3>
        <div
          v-if="frontendCompatible"
          class="flex items-center space-x-1 text-xs text-green-600"
        >
          <BoltIcon class="w-3 h-3" />
          <span>Frontend Compatible</span>
        </div>
      </div>
      <div class="flex items-center space-x-3">
        <div class="text-sm text-gray-600">
          Complexity:
          <span :class="`text-${styleComplexity.color}-600 font-medium`">
            {{ styleComplexity.label }}
          </span>
        </div>
        <button
          @click="resetStyles"
          class="text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Reset
        </button>
      </div>
    </div>

    <!-- Style Toggle -->
    <div
      class="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200"
    >
      <div>
        <h4 class="font-medium text-purple-900">Enable Custom Styling</h4>
        <p class="text-sm text-purple-700">
          Transform your QR code with modern designs
        </p>
        <div class="flex items-center space-x-4 mt-2 text-xs">
          <div class="flex items-center space-x-1">
            <DevicePhoneMobileIcon class="w-3 h-3" />
            <span
              :class="frontendCompatible ? 'text-green-600' : 'text-yellow-600'"
            >
              Frontend: {{ frontendCompatible ? "✅" : "⚠️" }}
            </span>
          </div>
          <div class="flex items-center space-x-1">
            <ServerIcon class="w-3 h-3" />
            <span class="text-green-600">Backend: ✅</span>
          </div>
        </div>
      </div>
      <button
        @click="toggleStyling"
        class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
        :class="{
          'bg-purple-600': localOptions.enabled,
          'bg-gray-200': !localOptions.enabled,
        }"
      >
        <span
          class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
          :class="{
            'translate-x-6': localOptions.enabled,
            'translate-x-1': !localOptions.enabled,
          }"
        />
      </button>
    </div>

    <!-- Style Sections (only show if styling is enabled) -->
    <div v-if="localOptions.enabled" class="space-y-6">
      <!-- Section Navigation -->
      <nav class="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          v-for="section in sections"
          :key="section.id"
          @click="activeSection = section.id"
          class="flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors flex-1 justify-center"
          :class="{
            'bg-white text-purple-700 shadow-sm': activeSection === section.id,
            'text-gray-500 hover:text-gray-700': activeSection !== section.id,
          }"
        >
          <component :is="section.icon" class="w-4 h-4" />
          <span>{{ section.label }}</span>
        </button>
      </nav>

      <!-- Presets Section -->
      <div v-if="activeSection === 'presets'" class="space-y-6">
        <!-- Quick Styles -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-3">
            Quick Styles
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              v-for="style in quickStyles"
              :key="style.id"
              @click="applyQuickStyle(style)"
              class="p-4 text-left border rounded-lg transition-all hover:border-purple-300"
              :class="{
                'border-purple-500 bg-purple-50':
                  hasActiveStyles &&
                  JSON.stringify(localOptions.dotsType) ===
                    JSON.stringify(style.style.dotsType),
                'border-gray-200': !(
                  hasActiveStyles &&
                  JSON.stringify(localOptions.dotsType) ===
                    JSON.stringify(style.style.dotsType)
                ),
              }"
            >
              <div class="flex items-center space-x-3">
                <span class="text-2xl">{{ style.icon }}</span>
                <div class="flex-1">
                  <div class="font-medium text-gray-900">{{ style.name }}</div>
                  <div class="text-sm text-gray-500">
                    {{ style.description }}
                  </div>
                  <div class="text-xs text-purple-600 mt-1">
                    {{ style.preview }}
                  </div>
                  <div class="flex items-center space-x-2 mt-1">
                    <div class="text-xs text-green-600">
                      <DevicePhoneMobileIcon class="w-3 h-3 inline mr-1" />
                      {{ getSupportIcon(style.frontendSupport) }}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        <!-- Style Presets -->
        <div v-if="availablePresets.length > 0">
          <label class="block text-sm font-medium text-gray-700 mb-3">
            Style Presets
          </label>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button
              v-for="preset in availablePresets"
              :key="preset"
              @click="applyPreset(preset)"
              class="p-3 text-center border rounded-lg transition-all hover:border-purple-300"
              :class="{
                'border-purple-500 bg-purple-50 text-purple-700':
                  localOptions.preset === preset,
                'border-gray-200 text-gray-700': localOptions.preset !== preset,
              }"
            >
              <div class="font-medium text-sm">{{ preset }}</div>
              <div class="text-xs opacity-75 mt-1">Click to apply</div>
            </button>
          </div>
        </div>
      </div>

      <!-- Advanced Section -->
      <div v-if="activeSection === 'advanced'" class="space-y-6">
        <div
          v-for="styleOption in advancedStyles"
          :key="styleOption.key"
          class="space-y-2"
        >
          <div class="flex items-center justify-between">
            <label class="block text-sm font-medium text-gray-700">
              {{ styleOption.name }}
            </label>
            <div class="text-xs text-green-600">
              <DevicePhoneMobileIcon class="w-3 h-3 inline mr-1" />
              {{ getSupportIcon(styleOption.frontendSupport) }}
            </div>
          </div>

          <select
            v-model="localOptions[styleOption.key]"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option
              v-for="option in styleOption.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }} - {{ option.description }}
            </option>
          </select>
        </div>

        <!-- Legacy Style Support -->
        <div class="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h4 class="font-medium text-yellow-800 mb-2 flex items-center">
            <InformationCircleIcon class="w-4 h-4 mr-2" />
            Legacy Style Options
          </h4>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              v-if="
                localOptions.dotsType === 'dots' || localOptions.type === 'dots'
              "
            >
              <label class="block text-sm text-yellow-700 mb-1">
                Dot Scale: {{ localOptions.dotScale }}
              </label>
              <input
                v-model.number="localOptions.dotScale"
                type="range"
                min="0.1"
                max="1.0"
                step="0.1"
                class="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div
              v-if="
                localOptions.dotsType === 'rounded' ||
                localOptions.type === 'rounded'
              "
            >
              <label class="block text-sm text-yellow-700 mb-1">
                Corner Radius: {{ localOptions.cornerRadius }}px
              </label>
              <input
                v-model.number="localOptions.cornerRadius"
                type="range"
                min="1"
                max="50"
                step="1"
                class="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Gradients Section -->
      <div v-if="activeSection === 'gradients'" class="space-y-6">
        <!-- Gradient Colors Editor -->
        <div>
          <div class="flex items-center justify-between mb-3">
            <label class="block text-sm font-medium text-gray-700">
              Gradient Colors
            </label>
            <div class="flex items-center space-x-2">
              <button
                @click="swapGradientColors"
                :disabled="localOptions.gradientColors.length < 2"
                class="text-sm text-purple-600 hover:text-purple-800 transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon class="w-4 h-4 inline mr-1" />
                Swap
              </button>
              <button
                @click="addGradientColor"
                :disabled="localOptions.gradientColors.length >= 5"
                class="text-sm text-purple-600 hover:text-purple-800 transition-colors disabled:opacity-50"
              >
                + Add Color
              </button>
            </div>
          </div>

          <!-- Color Inputs -->
          <div class="space-y-3">
            <div
              v-for="(color, index) in localOptions.gradientColors"
              :key="index"
              class="flex items-center space-x-3"
            >
              <div class="flex items-center space-x-2 flex-1">
                <input
                  v-model="localOptions.gradientColors[index]"
                  type="color"
                  class="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  v-model="localOptions.gradientColors[index]"
                  type="text"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                  :placeholder="`Color ${index + 1}`"
                />
              </div>
              <button
                v-if="localOptions.gradientColors.length > 2"
                @click="removeGradientColor(index)"
                class="text-red-600 hover:text-red-800 transition-colors"
              >
                ✕
              </button>
            </div>
          </div>

          <!-- Gradient Controls -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label class="block text-sm text-gray-700 mb-2"
                >Gradient Type</label
              >
              <select
                v-model="localOptions.gradientType"
                class="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="linear">Linear</option>
                <option value="radial">Radial</option>
              </select>
            </div>

            <div v-if="localOptions.gradientType === 'linear'">
              <label class="block text-sm text-gray-700 mb-2">
                Rotation: {{ localOptions.gradientRotation }}°
              </label>
              <input
                v-model.number="localOptions.gradientRotation"
                type="range"
                min="0"
                max="360"
                step="15"
                class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>

        <!-- Gradient Presets -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-3">
            Gradient Presets
          </label>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              v-for="preset in gradientPresets"
              :key="preset.name"
              @click="applyGradientPreset(preset)"
              class="p-3 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
            >
              <div class="flex items-center space-x-2 mb-2">
                <div
                  class="w-4 h-4 border border-gray-300 rounded"
                  :style="{
                    background: `linear-gradient(${preset.rotation || 0}deg, ${
                      preset.colors[0]
                    }, ${preset.colors[1]})`,
                  }"
                ></div>
                <span class="text-sm font-medium">{{ preset.name }}</span>
              </div>
              <div class="flex">
                <div
                  v-for="color in preset.colors"
                  :key="color"
                  class="flex-1 h-2 first:rounded-l last:rounded-r"
                  :style="{ backgroundColor: color }"
                ></div>
              </div>
            </button>
          </div>
        </div>

        <!-- Gradient Preview -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Gradient Preview
          </label>
          <div
            class="w-full h-20 border border-gray-300 rounded-lg"
            :style="{
              background:
                localOptions.gradientColors.length > 1
                  ? `${localOptions.gradientType}-gradient(${
                      localOptions.gradientType === 'linear'
                        ? `${localOptions.gradientRotation}deg, `
                        : ''
                    }${localOptions.gradientColors.join(', ')})`
                  : localOptions.gradientColors[0] || '#f3f4f6',
            }"
          ></div>
        </div>
      </div>

      <!-- Colors Section -->
      <div v-if="activeSection === 'colors'" class="space-y-6">
        <!-- Individual Color Controls -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Dark Color Override
            </label>
            <div class="flex items-center space-x-2">
              <input
                v-model="localOptions.darkColor"
                type="color"
                class="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                v-model="localOptions.darkColor"
                type="text"
                class="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                placeholder="Inherit from QR options"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Light Color Override
            </label>
            <div class="flex items-center space-x-2">
              <input
                v-model="localOptions.lightColor"
                type="color"
                class="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                v-model="localOptions.lightColor"
                type="text"
                class="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                placeholder="Inherit from QR options"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Background Color
            </label>
            <div class="flex items-center space-x-2">
              <input
                v-model="localOptions.backgroundColor"
                type="color"
                class="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                v-model="localOptions.backgroundColor"
                type="text"
                class="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                placeholder="Transparent"
              />
            </div>
          </div>
        </div>

        <!-- Color Presets -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-3">
            Color Themes
          </label>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              v-for="preset in colorPresets"
              :key="preset.name"
              @click="applyColorPreset(preset)"
              class="p-3 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
            >
              <div class="flex items-center space-x-2 mb-2">
                <div class="flex">
                  <div
                    class="w-4 h-4 border border-gray-300 rounded-l"
                    :style="{ backgroundColor: preset.dark }"
                  ></div>
                  <div
                    class="w-4 h-4 border border-gray-300"
                    :style="{ backgroundColor: preset.light }"
                  ></div>
                  <div
                    class="w-4 h-4 border border-gray-300 rounded-r"
                    :style="{ backgroundColor: preset.bg }"
                  ></div>
                </div>
                <span class="text-sm font-medium">{{ preset.name }}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Color Info -->
        <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 class="font-medium text-blue-800 mb-2">Color Override Info</h4>
          <div class="text-sm text-blue-700 space-y-1">
            <p>• Dark/Light color overrides take precedence over QR options</p>
            <p>• Background color is separate from the QR module colors</p>
            <p>• Leave fields empty to inherit from main QR options</p>
            <p>• Gradients will override dark color settings</p>
          </div>
        </div>
      </div>

      <!-- Style Preview Toggle -->
      <div
        class="flex items-center justify-between pt-4 border-t border-gray-200"
      >
        <button
          @click="showPreview = !showPreview"
          class="flex items-center space-x-2 text-sm text-purple-600 hover:text-purple-800 transition-colors"
        >
          <BeakerIcon class="w-4 h-4" />
          <span>{{ showPreview ? "Hide" : "Show" }} Style Preview</span>
        </button>

        <div class="text-xs text-gray-500">
          {{ hasActiveStyles ? "Custom styling active" : "No custom styling" }}
        </div>
      </div>

      <!-- Style Preview -->
      <div
        v-if="showPreview && hasActiveStyles"
        class="bg-gray-50 p-4 rounded-lg border border-gray-200"
      >
        <h4 class="font-medium text-gray-800 mb-3">
          Current Style Configuration
        </h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div v-if="localOptions.preset">
            <span class="text-gray-500">Preset:</span>
            <span class="ml-1 font-medium text-purple-600">{{
              localOptions.preset
            }}</span>
          </div>
          <div v-if="localOptions.dotsType !== 'square'">
            <span class="text-gray-500">Dots:</span>
            <span class="ml-1 font-medium">{{ localOptions.dotsType }}</span>
          </div>
          <div v-if="localOptions.cornersSquareType !== 'square'">
            <span class="text-gray-500">Corners:</span>
            <span class="ml-1 font-medium">{{
              localOptions.cornersSquareType
            }}</span>
          </div>
          <div v-if="localOptions.gradientColors.length > 0">
            <span class="text-gray-500">Gradient:</span>
            <span class="ml-1 font-medium"
              >{{ localOptions.gradientType }} ({{
                localOptions.gradientColors.length
              }}
              colors)</span
            >
          </div>
          <div v-if="localOptions.darkColor">
            <span class="text-gray-500">Dark Color:</span>
            <span class="ml-1 font-medium font-mono">{{
              localOptions.darkColor
            }}</span>
          </div>
          <div v-if="localOptions.backgroundColor">
            <span class="text-gray-500">Background:</span>
            <span class="ml-1 font-medium font-mono">{{
              localOptions.backgroundColor
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Disabled State Message -->
    <div v-else class="text-center py-8 text-gray-500">
      <SparklesIcon class="w-12 h-12 mx-auto mb-3 text-gray-300" />
      <p class="text-lg font-medium">Custom styling is disabled</p>
      <p class="text-sm">
        Enable styling above to access advanced QR customization options
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Custom color input styling */
input[type="color"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

input[type="color"]::-webkit-color-swatch-wrapper {
  padding: 0;
  border-radius: 6px;
  border: 1px solid #d1d5db;
}

input[type="color"]::-webkit-color-swatch {
  border: none;
  border-radius: 6px;
}

input[type="color"]::-moz-color-swatch {
  border: none;
  border-radius: 6px;
}

/* Custom range slider styling */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
}

input[type="range"]::-webkit-slider-track {
  background: #e5e7eb;
  height: 8px;
  border-radius: 4px;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #8b5cf6;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

input[type="range"]::-moz-range-track {
  background: #e5e7eb;
  height: 8px;
  border-radius: 4px;
  border: none;
}

input[type="range"]::-moz-range-thumb {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #8b5cf6;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  border: none;
}

/* Focus styles for accessibility */
button:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

input:focus {
  outline: none;
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

/* Toggle switch styling */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

/* Gradient preview styling */
.gradient-preview {
  transition: background 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
  }

  .grid-cols-4 {
    grid-template-columns: repeat(2, 1fr);
  }

  .grid-cols-3 {
    grid-template-columns: 1fr;
  }
}

/* Animation for style changes */
@keyframes styleApplied {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.style-applied {
  animation: styleApplied 0.3s ease;
}
</style>